import React, { useState, useEffect } from 'react';
import { categoryService } from '../services/categoryService';

function BudgetTracker({ expenses }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await categoryService.getCategories();
      const hierarchicalCategories = data.categories || [];
      
      // Flatten the categories to include both parents and subcategories
      const flattenedCategories = [];
      hierarchicalCategories.forEach(parent => {
        flattenedCategories.push(parent);
        if (parent.subcategories && parent.subcategories.length > 0) {
          flattenedCategories.push(...parent.subcategories);
        }
      });
      
      setCategories(flattenedCategories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategorySpending = (categoryName, categoryData) => {
    if (!expenses || expenses.length === 0) return 0;
    
    // Check if this category has subcategories (meaning it's a parent)
    const hasSubcategories = categoryData?.subcategories && categoryData.subcategories.length > 0;
    const subcategoryNames = hasSubcategories ? categoryData.subcategories.map(sub => sub.name) : [];
    
    let total = 0;
    
    expenses.forEach(expense => {
      // Handle split expenses
      if (expense.splits && Array.isArray(expense.splits)) {
        expense.splits.forEach(split => {
          if (hasSubcategories) {
            // For parent categories: include parent AND subcategory expenses
            if (split.categoryId === categoryName || subcategoryNames.includes(split.categoryId)) {
              total += split.amount || 0;
            }
          } else {
            // For subcategories or leaf categories: only include exact matches
            if (split.categoryId === categoryName) {
              total += split.amount || 0;
            }
          }
        });
      } else {
        // Handle simple expenses
        if (hasSubcategories) {
          // For parent categories: include parent AND subcategory expenses
          if (expense.category === categoryName || subcategoryNames.includes(expense.category)) {
            total += expense.amount || 0;
          }
        } else {
          // For subcategories or leaf categories: only include exact matches
          if (expense.category === categoryName) {
            total += expense.amount || 0;
          }
        }
      }
    });
    
    return total;
  };

  // Only show categories that have budgets AND have expenses in the current filtered view
  const categoriesWithBudgets = categories.filter(cat => {
    if (!cat.budgetLimit || cat.budgetLimit <= 0) return false;
    
    if (!expenses || expenses.length === 0) return false;
    
    // Check if this category has subcategories (meaning it's a parent)
    const hasSubcategories = cat.subcategories && cat.subcategories.length > 0;
    const subcategoryNames = hasSubcategories ? cat.subcategories.map(sub => sub.name) : [];
    
    // Check if this category or any of its subcategories have expenses
    const hasExpenses = expenses.some(expense => {
      // Check split expenses
      if (expense.splits && Array.isArray(expense.splits)) {
        return expense.splits.some(split => {
          if (hasSubcategories) {
            // For parent categories: check parent AND subcategories
            return split.categoryId === cat.name || subcategoryNames.includes(split.categoryId);
          } else {
            // For subcategories or leaf categories: only check exact match
            return split.categoryId === cat.name;
          }
        });
      }
      // Check simple expenses
      if (hasSubcategories) {
        // For parent categories: check parent AND subcategories
        return expense.category === cat.name || subcategoryNames.includes(expense.category);
      } else {
        // For subcategories or leaf categories: only check exact match
        return expense.category === cat.name;
      }
    });
    
    return hasExpenses;
  });

  if (loading) {
    return (
      <div style={{ marginBottom: '24px', padding: '16px', color: '#b0b0b0' }}>
        Loading budget data...
      </div>
    );
  }

  if (categoriesWithBudgets.length === 0) {
    // Check if there are categories with budgets but no expenses
    const categoriesWithBudgetsButNoExpenses = categories.filter(cat => cat.budgetLimit && cat.budgetLimit > 0);
    
    if (categoriesWithBudgetsButNoExpenses.length > 0) {
      return (
        <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(52, 152, 219, 0.1)', borderRadius: '8px' }}>
          <h3 style={{ color: '#3498db', margin: '0 0 8px 0' }}>Budget Tracking</h3>
          <p style={{ color: '#b0b0b0', margin: 0, fontSize: '14px' }}>
            No budget categories have expenses in the current view. {categoriesWithBudgetsButNoExpenses.length} category{categoriesWithBudgetsButNoExpenses.length !== 1 ? 'ies' : 'y'} with budget limits {categoriesWithBudgetsButNoExpenses.length !== 1 ? 'are' : 'is'} available but {categoriesWithBudgetsButNoExpenses.length !== 1 ? 'have' : 'has'} no expenses to track.
          </p>
        </div>
      );
    }
    
    return (
      <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(255, 107, 53, 0.1)', borderRadius: '8px' }}>
        <h3 style={{ color: '#ff6b35', margin: '0 0 8px 0' }}>Budget Tracking</h3>
        <p style={{ color: '#b0b0b0', margin: 0, fontSize: '14px' }}>
          No categories with budget limits found. Add budget limits to your categories to track spending.
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ 
          color: '#f0f0f0', 
          margin: '0 0 4px 0',
          fontSize: '1.3rem',
          fontWeight: '600'
        }}>
          Budget Tracking
        </h3>
        <p style={{ 
          color: '#b0b0b0', 
          margin: 0, 
          fontSize: '14px' 
        }}>
          Showing budget progress for categories with expenses ({expenses?.length || 0} expense{expenses?.length !== 1 ? 's' : ''})
        </p>
      </div>
      
      {(!expenses || expenses.length === 0) && (
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px', 
          background: 'rgba(52, 152, 219, 0.1)', 
          borderRadius: '8px',
          border: '1px solid rgba(52, 152, 219, 0.2)'
        }}>
          <p style={{ color: '#3498db', margin: 0, fontSize: '14px' }}>
            No expenses found. Budget tracking will show progress based on your current expense list.
          </p>
        </div>
      )}
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '16px' 
      }}>
        {categoriesWithBudgets.map((category) => {
          const spent = getCategorySpending(category.name, category);
          const budget = category.budgetLimit;
          const percentage = budget > 0 ? (spent / budget) * 100 : 0;
          const isOverBudget = spent > budget;
          const isNearLimit = percentage > 80 && !isOverBudget;
          
          return (
            <div 
              key={category.id}
              style={{
                background: isOverBudget 
                  ? 'rgba(255, 71, 87, 0.1)' 
                  : isNearLimit 
                    ? 'rgba(255, 193, 7, 0.1)'
                    : 'rgba(46, 213, 115, 0.1)',
                border: `1px solid ${isOverBudget 
                  ? 'rgba(255, 71, 87, 0.3)' 
                  : isNearLimit 
                    ? 'rgba(255, 193, 7, 0.3)'
                    : 'rgba(46, 213, 115, 0.3)'}`,
                borderRadius: '12px',
                padding: '16px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px' 
              }}>
                <span style={{ 
                  fontSize: '20px', 
                  marginRight: '8px' 
                }}>
                  {category.icon}
                </span>
                <h4 style={{ 
                  margin: '0', 
                  color: '#f0f0f0',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}>
                  {category.name}
                </h4>
                {isOverBudget && (
                  <span style={{ 
                    marginLeft: 'auto',
                    fontSize: '16px'
                  }}>
                    ⚠️
                  </span>
                )}
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                  <span style={{ 
                    fontSize: '14px', 
                    color: '#b0b0b0' 
                  }}>
                    Spent: ${spent.toLocaleString()}
                  </span>
                  <span style={{ 
                    fontSize: '14px', 
                    color: '#b0b0b0' 
                  }}>
                    Budget: ${budget.toLocaleString()}
                  </span>
                </div>
                
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min(percentage, 100)}%`,
                    height: '100%',
                    backgroundColor: isOverBudget 
                      ? '#ff4757' 
                      : isNearLimit 
                        ? '#ffc107'
                        : '#2ed573',
                    transition: 'width 0.3s ease',
                    borderRadius: '4px'
                  }}></div>
                </div>
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ 
                  fontSize: '12px', 
                  color: isOverBudget 
                    ? '#ff4757' 
                    : isNearLimit 
                      ? '#ffc107'
                      : '#2ed573',
                  fontWeight: '600'
                }}>
                  {percentage.toFixed(1)}% used
                </span>
                <span style={{ 
                  fontSize: '12px', 
                  color: isOverBudget ? '#ff4757' : '#2ed573',
                  fontWeight: '600'
                }}>
                  {isOverBudget 
                    ? `$${(spent - budget).toLocaleString()} over`
                    : `$${(budget - spent).toLocaleString()} left`
                  }
                </span>
              </div>
              
              {isOverBudget && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(255, 71, 87, 0.2)',
                  color: '#ff4757',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  OVER BUDGET
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BudgetTracker;